class AutoFontSize {
    // Static property compatibility for legacy environments
    static _groups = typeof AutoFontSize._groups !== 'undefined' ? AutoFontSize._groups : new Map();

    /**
     * new AutoFontSize(el, settings)
     * settings:
     *   - number  => {min: number}
     *   - object  => {min?: number, max?: number}
     * Defaults: {min: 1, max: initialComputedSize}
     */
    constructor(el, settings = {}) {
        if (!el) throw new Error("AutoFontSize: element is required");
        this.el = el;

        // Initial computed px (before any changes)
        const initialPx = (() => {
            const n = parseFloat(getComputedStyle(el).fontSize);
            return Number.isFinite(n) ? Math.max(1, Math.round(n)) : 16;
        })();
        this._initialPx = initialPx;

        // Options
        if (typeof settings === "number") {
            this.opts = {min: Math.max(1, settings), max: initialPx};
        } else {
            this.opts = {
                min: Math.max(1, settings.min ?? 1),
                max: (typeof settings.max === "number") ? settings.max : initialPx
            };
        }

        // Preserve original inline font-size for destroy()
        this._originalFontSize = el.style.fontSize || "";

        // Hidden measurer for single-line width
        this._measurer             = document.createElement("span");
        this._measurer.textContent = el.innerText || el.textContent || "";
        Object.assign(this._measurer.style, {
            position: "absolute",
            visibility: "hidden",
            whiteSpace: "nowrap",
            padding: "0",
            margin: "0",
            border: "0",
            left: "-99999px",
            top: "0"
        });

        // Copy key font/text styles
        const cs = getComputedStyle(el);
        ["font-family", "font-style", "font-weight", "font-stretch", "font-variant", "letter-spacing", "text-transform", "line-height"]
            .forEach(p => this._measurer.style[p] = cs.getPropertyValue(p));

        document.body.appendChild(this._measurer);

        // React to element size, window size, and text changes
        this._ro = new ResizeObserver(() => this._schedule());
        this._ro.observe(this.el);
        window.addEventListener("resize", this._onWinResize = () => this._schedule(), {passive: true});

        this._mo = new MutationObserver(() => {
            this._measurer.textContent = this.el.innerText || this.el.textContent || "";
            this._schedule();
        });
        this._mo.observe(this.el, {childList: true, characterData: true, subtree: true});

        // Determine ref for grouped instances
        this.ref = typeof settings === "object" && settings.ref ? settings.ref : null;

        // Register in group if ref is set
        if (this.ref) {
            if (!AutoFontSize._groups.has(this.ref)) {
                AutoFontSize._groups.set(this.ref, []);
            }
            AutoFontSize._groups.get(this.ref).push(this);
        }

        // Track measurer for cleanup
        AutoFontSize._measurers = AutoFontSize._measurers || [];
        AutoFontSize._measurers.push(this._measurer);
        this.fit();


    }

    fit() {
        const {min, max} = this.opts;
        // Skip fitting if element is hidden
        if (this.el.clientWidth === 0) return;

        if (this.ref) {
            // Group fit: find the largest size that fits all
            const group = AutoFontSize._groups.get(this.ref) || [];
            let bestPx  = min;
            for (let px = max; px >= min; px--) {
                if (group.every(inst => inst._fits(px))) {
                    bestPx = px;
                    break;
                }
            }
            group.forEach(inst => inst._apply(bestPx));
            group.forEach(inst => inst.el.classList.add("auto-font-size"));
        } else {
            let curPx   = this._currentFontPx();
            const lower = Math.max(1, Math.floor(min));
            const upper = Math.max(lower, Math.floor(max));

            // Always cap at max
            if (curPx > upper) {
                curPx = upper;
                this._apply(curPx);
            }

            if (this._fits(curPx)) {
                // If it fits now, try to grow up to max (useful after prior shrink)
                if (curPx < upper) {
                    this._apply(this._binarySearch(curPx, upper));
                }
                this.el.classList.add("auto-font-size");
                return;
            }

            // Shrink until it fits, but not below min
            const best = this._binarySearch(lower, Math.min(curPx, upper));

            this._apply(best);

            this.el.classList.add("auto-font-size");
        }
    }

    destroy() {
        this._ro?.disconnect();
        this._mo?.disconnect();
        window.removeEventListener("resize", this._onWinResize);
        this._measurer?.remove();
        this.el.style.fontSize = this._originalFontSize;

        // Remove from group if ref is set
        if (this.ref) {
            const group = AutoFontSize._groups.get(this.ref);
            if (group) {
                const filtered = group.filter(inst => inst !== this);
                if (filtered.length === 0) {
                    AutoFontSize._groups.delete(this.ref);
                } else {
                    AutoFontSize._groups.set(this.ref, filtered);
                }
            }
        }
    }

    // ---- internals ----
    _schedule() {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.fit());
    }

    _currentFontPx() {
        const n = parseFloat(getComputedStyle(this.el).fontSize);
        return Number.isFinite(n) ? Math.max(1, Math.round(n)) : 16;
    }

    _apply(px) {
        this.el.style.fontSize = `${px}px`;
    }

    _fits(px) {
        this._measurer.style.fontSize = `${px}px`;
        this._measurer.textContent    = this.el.textContent || "";
        const textWidth               = this._measurer.offsetWidth;
        const boxWidth                = this.el.clientWidth; // includes padding, matches text layout box
        return textWidth <= boxWidth;
    }

    _binarySearch(lo, hi) {
        let best = lo;
        while (lo <= hi) {
            const mid = Math.floor((lo + hi) / 2);
            if (this._fits(mid)) {
                best = mid;
                lo   = mid + 1;
            } else {
                hi = mid - 1;
            }
        }
        return best;
    }
}

/* Usage:
   new AutoFontSize(h2);                     // min=1, max=initial
   new AutoFontSize(h2, 20);                 // min=20, max=initial
   new AutoFontSize(h2, { min:18 });         // min=18, max=initial
   new AutoFontSize(h2, { min:18, max:64 }); // min=18, max=64
   // instance.destroy() to clean up
*/


icing.watch('[data-auto-font-size]', el => {
    let options = 1;
    try {
        options = JSON.parse(el.dataset.autoFontSize);
    } catch {}
    new AutoFontSize(el, options);
});
